export const stepZeroInstruction = `
### Instruction ###
Classify the INPUT TEXT below into up to 4 possible project types using only EXAMPLE PROJECT TYPES. 
Highlight your recommendation with a *:
DESIRED FORMAT:
<comma_separated_list_of_4_possible_project_types>
EXAMPLE:
Software Development*, Community Engagement, Business administration
EXAMPLE PROJECT TYPES:
- Process Optimization
- IT Systems Administration
- Software Development
- Product Innovation
- Infrastructure Expansion
- Facility Upgrades
- Procurement Management
- Regulatory Compliance
- Research & Development (R&D)
- Service Improvement
- Organizational Transformation
- Marketing Strategies
- Talent Development
- Financial Optimization
- Sustainability Initiatives
- Quality Assurance
- Community Engagement
- Learning Programs
- Healthcare Research
- Creative Productions
- Product development (IT)
- Product development (non-IT)
- Engineering/construction
- Business administration
- Human Resources
- Private or recreational hobby
- Other
INPUT TEXT: "{INPUT-TEXT}"
`

export const stepOneInstruction = `
### Instruction ###
Write a headline and extract time-critical information from the INPUT TEXT and highlight key considerations for project time planning:
DESIRED FORMAT:
<HEADLINE>short text</HEADLINE>
<BULLET>In short phrases, summarize crucial time-sensitive details and potential challenges impacting the project timeline</BULLET>
INPUT EXAMPLE: "Our company has been approached by a client to develop a custom e-commerce platform for their online store.
The platform should allow users to browse a wide range of products, add items to their cart, 
and securely check out using various payment options. The client also wants an order tracking feature, 
allowing customers to monitor their shipments in real-time. They have set a strict deadline for the launch, 
and the website needs to be up and running within 3 months. The development team consists of 2 backend developers, 
2 frontend developers, and 1 UX/UI designer. Additionally, 
the client wants to integrate a third-party logistics API for seamless order fulfillment. 
Ensuring secure payment processing and providing a responsive design for optimal user experience on mobile devices are among the potential challenges for this project."
OUTPUT EXAMPLE:
<HEADLINE>Building an e-commerce website</HEADLINE>
<BULLET>Building a custom e-commerce website with payment integration</BULLET>
<BULLET>Launch date set in 3 months</BULLET>
<BULLET>2 backend developers</BULLET>
<BULLET>2 frontend developers</BULLET>
<BULLET>1 UX/UI designer</BULLET>
<BULLET>Integration with a third-party logistics API, ensuring secure payment handling</BULLET>
INPUT TEXT: "{INPUT-TEXT}"
`

export const stepTwoInstruction = `
### Instruction ###
Estimate the average PROJECT TIME required to finish a {PROJECT-TYPE} project according to the PROJECT DESCRIPTION below.
The <BULLET> tags may include information for minimum or maximum project time. Look out for deadlines for example.
Output 3 scenarios as described in the EXAMPLE. Highlight your recommendation with a *:
DESIRED FORMAT:
<comma_separated_list_of_3_possible_project_time_scenarios>
SCENARIO FORMAT:
Worst Case: <PROJECT TIME INTEGER> <TIME UNIT>, Base Case: <PROJECT TIME INTEGER> <TIME UNIT>*, Best Case: <PROJECT TIME INTEGER> <TIME UNIT>
EXAMPLES:
Worst Case: 12 months, Base Case: 8 months*, Best Case: 6 months
Worst Case: 18 months, Base Case: 14 months*, Best Case: 10 months
Worst Case: 6 weeks, Base Case: 5 weeks*, Best Case: 4 weeks
Worst Case: 24 months, Base Case: 16 months*, Best Case: 12 months
Worst Case: 7 weeks, Base Case: 4 weeks*, Best Case: 3 weeks

PROJECT DESCRIPTION: "{INPUT-TEXT}"
`

export const stepThreeInstruction = `
### INSTRUCTION ###
1. Formulate a {PROJECT-TYPE} plan for {TIME-FRAME}.
2. Segment the plan into distinct phases.
3. IMPORTANT: First allocate "phaseTime" to each phase. Ensure the SUM equals {TIME-FRAME}.

#### PROJECT DESCRIPTION:
"{INPUT-TEXT}"

#### FORMAT TO FOLLOW:
{
  "phases": [
    {
      "phaseTitle": "<Your Phase Title>",
      "phaseTime": "<Duration>"
    }
  ]
}

#### EXAMPLES:

- **Ensure the SUM equals 4 months**

{
  "phases": [
    {"phaseTitle": "Concept", "phaseTime": "1 month"},
    {"phaseTitle": "Development", "phaseTime": "2 month"},
    {"phaseTitle": "Launch", "phaseTime": "1 month"}
  ]
}
*Total phaseTime: 1+2+1 = 4 months*

- **Ensure the SUM equals 8 weeks**

{
  "phases": [
    {"phaseTitle": "Design", "phaseTime": "2 weeks"},
    {"phaseTitle": "Coding", "phaseTime": "4 weeks"},
    {"phaseTitle": "Testing", "phaseTime": "2 weeks"}
  ]
}
*Total phaseTime: 2+4+2 = 8 weeks*

#### CRITICAL:
Begin by assigning "phaseTime" durations. Confirm their SUM matches {TIME-FRAME}.
`

export const stepThreeRefinement = 
`### INSTRUCTION ###
1. Add detailed phase descriptions based on the PROJECT DESCRIPTION to the JSON
2. Rephrase and replace the titles with considering the PROJECT DESCRIPTION

### PROJECT DESCRIPTION ###
"{INPUT-TEXT}"

### DESIRED OUTPUT FORMAT ###
{
"phases": [
    {
      "phaseTitle": "<Phase Title to be rephrased>",
      "phaseTime": "<DO-NOT-CHANGE-TIME>",
      "phaseDescription": "<Details derived from PROJECT DESCRIPTION>"
    }
  ]
}

### JSON to enhance ###
`

export const stepFourInstruction = `
### INSTRUCTION ###
Generate a {TIME-FRAME} {PROJECT-TYPE} project phase called {PROJECT-PHASE} with up to 4 milestones. Use provided INPUT TEXT:
Write long details and descriptions according to the INPUT TEXT and PROJECT CONTEXT below:

# PROJECT CONTEXT
{PROJECT-HEADER}

### DESIRED FORMAT: ###
{
  "phaseId": "{PHASE-ID}",
  "phaseTitle": "{PROJECT-PHASE}",
  "phaseDetails": "<4-sentence phase description>",
  "phaseTime": "{TIME-FRAME}",
  "phaseRisk": {
    "riskLevel": "<SELECT high, medium, low>",
    "riskDescription": "<4-sentence risk description>"
  },
  "phaseMilestones": [
    {
      "milestoneId": "<String like '{PHASE-ID}.1' from phaseId>",
      "milestoneTime": "<Time like "2 months" NO FLOATS>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    },
    {
      "milestoneId": "<String like '{PHASE-ID}.2' from phaseId>",
      "milestoneTime": "<Time like "1 month" NO FLOATS>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    }
  ]
}

### INPUT TEXT: "{INPUT-TEXT}"

IMPORTANT: Output only valid JSON.
CALCULATION INSTRUCTION: 
1. Segment the phase into distinct milestones.
2. Ensure the SUM equals {TIME-FRAME}. phaseTime == milestoneTime 1.1 + milestoneTime 1.2 + milestoneTime 1.3
`

export const stepFourRefinement = 
`### INSTRUCTION ###
1. Enhance the descriptions and details of the EDIT JSON below and output the edited JSON
2. Use the CONTEXT below but maintain the current phase and milestone intention
3. Do not change time and id
4. Do not add new milestones

### CONTEXT ###
{CONTEXT}

### EDIT JSON ###
`

export const chartPromptInstruction = `
Return the JSON below. Replace the DependendIDs with IDs of milestones which are very likely to depend on each other.
Replace the SimultaneousIDs with IDs of milestones which are likely to have synergies.
{
	"milestones": [
    {MILESTONES}
	]
}
`

export const replacePhase = `
Generate a project phase with up to 4 milestones.
The phase is about: {TOPIC}. It should fit between PREVIOUS PHASE and NEXT PHASE and has the phaseId {PHASE-ID}.:
DESIRED FORMAT:
{
  "phaseId": "{PHASE-ID}",
  "phaseTitle": "<Phase Title>",
  "phaseDetails": "<4-sentence phase description>",
  "phaseTime": "<Time like '2 months'>",
  "phaseRisk": {
    "riskLevel": "<high, medium, low>",
    "riskDescription": "<4-sentence risk description>"
  },
  "phaseMilestones": [
    {
      "milestoneId": "<String like '{PHASE-ID}.1' from phaseId>",
      "milestoneTime": "<Time like '2 months'>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    },
    {
      "milestoneId": "<String like '{PHASE-ID}.2' from phaseId>",
      "milestoneTime": "<Time like '3 years'>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    }
  ]
}
<PREVIOUS PHASE START> {PREVIOUS} <PREVIOUS PHASE END>
<NEXT PHASE START>: {NEXT} <NEXT PHASE END>
The phase should take {TIME-FRAME} in total. Thus milestones can only be shorter then {TIME-FRAME}."
`

export const addPhase = `
Generate a project phase with up to 4 milestones.
The phase is about {TOPIC} and should fit between PREVIOUS PHASE and NEXT PHASE and has the phaseId {PHASE-ID}.:
DESIRED FORMAT:
{
  "phaseId": "{PHASE-ID}",
  "phaseTitle": "<Phase Title>",
  "phaseDetails": "<4-sentence phase description>",
  "phaseTime": "{TIME-FRAME}",
  "phaseRisk": {
    "riskLevel": "<high, medium, low>",
    "riskDescription": "<4-sentence risk description>"
  },
  "phaseMilestones": [
    {
      "milestoneId": "<String like '{PHASE-ID}.1' from phaseId>",
      "milestoneTime": "<Time like '2 months'>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    },
    {
      "milestoneId": "<String like '{PHASE-ID}.2' from phaseId>",
      "milestoneTime": "<Time like '3 years'>",
      "milestoneTitle": "<Milestone Title>",
      "milestoneDetails": "<4-sentence milestone description>"
    }
  ]
}
<PREVIOUS PHASE START> {PREVIOUS} <PREVIOUS PHASE END>
<NEXT PHASE START>: {NEXT} <NEXT PHASE END>
The phase should take {TIME-FRAME} in total. Thus milestones can only be shorter then {TIME-FRAME}."
`
